import { CONTENT_TYPE } from '@yandex-int/k-common/typings/api/models/content';
import { ActivityViewSettings, AdditionalInfo } from '@yandex-int/k-common/typings/api/models/main-page';
import { CheckCircle } from 'education-icons';
import {
    Button,
    Image,
    SimpleText,
    SUBJECTS_SLUGS_TYPE,
    Text,
    useGeneratePath,
    useStaticUrls,
} from 'platform-components';
import { getQuizConfig } from 'platform-components/src/components/app-factory/selectors';
import { UI_KIT_TEXT_TYPOGRAPHY } from 'platform-components/src/components/constants';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation, useParams } from 'react-router';
import ym from 'react-yandex-metrika';

import { ActivityTypeIcon } from 'common.components/activity-type-icon/activity-type-icon';
import { actions as lessonActions } from 'common.components/lesson';
import * as lessonViewActions from 'common.components/lesson-view.store/actions';
import { ShowcaseMetrikaParams } from 'common.components/typings';
import { getDefaultCourseId } from 'store/courses/selectors';
import { getPrepLessonsDetails } from 'store/prep-lessons/selectors';

import './actual-content__activity.scss';

interface Props {
    contentType: CONTENT_TYPE;
    contentId?: string;
    viewSettings?: ActivityViewSettings;
    subject?: SUBJECTS_SLUGS_TYPE;
    description?: string;
    isLoading?: boolean;
    metrikaParams?: ShowcaseMetrikaParams;
    additionalInfo?: AdditionalInfo;
}

// eslint-disable-next-line complexity
export const ActualContentActivity = (props: Props) => {
    const { contentType, contentId, subject, viewSettings, description, isLoading, metrikaParams, additionalInfo } =
        props;
    const { icon, name: title, description: tagDescription } = viewSettings || {};
    const { url, description: additionalDescription, is_open_in_new_tab } = additionalInfo || {};

    const history = useHistory();
    const { search } = useLocation();
    const dispatch = useDispatch();
    const staticUrls = useStaticUrls();
    const { classId } = useParams<Record<string, string>>();
    const teamId = Number(classId);
    const generatePath = useGeneratePath();
    const quizConfig = useSelector(getQuizConfig);
    const courseId = useSelector((store) => getDefaultCourseId(store, { subject, classId: teamId }));
    const prepLessonDetails = useSelector((store) => getPrepLessonsDetails(store, { courseId }));
    const isLesson = contentType === CONTENT_TYPE.LESSON && Boolean(contentId);
    const isQuiz = contentType === CONTENT_TYPE.QUIZ && Boolean(contentId);
    const isLink = contentType === CONTENT_TYPE.LINK && Boolean(contentId);
    const linkDescription = isLink && additionalDescription;
    const quiz = isQuiz ? quizConfig.quizzes.find(({ id }) => id === contentId) : null;
    const isAssignedLesson = isLesson && prepLessonDetails[Number(contentId)]?.assignedCount > 0;
    const cantProceed = !isLoading && isAssignedLesson;
    const buttonTextId = cantProceed
        ? `library.mainPage.actualContent.${contentType}.buttonText.done`
        : `library.mainPage.actualContent.${contentType}.buttonText`;
    const activityDescription = quiz?.title || description || linkDescription || tagDescription;

    // eslint-disable-next-line complexity
    const handleClick = () => {
        ym('reachGoal', 'teacher_main_week_click_activity', { activityType: contentType, contentId, ...metrikaParams });
        switch (contentType) {
            case CONTENT_TYPE.LESSON: {
                if (contentId) {
                    dispatch(
                        lessonViewActions.loadLessonViewData({
                            lessonId: Number(contentId),
                            courseId,
                            onSuccess: () => {
                                dispatch(lessonActions.setSubject({ subject }));
                            },
                        })
                    );
                    dispatch(lessonViewActions.toggleModal(true));
                }
                break;
            }
            case CONTENT_TYPE.QUIZ: {
                if (window) {
                    window.open(generatePath(staticUrls.labQuizGame, { quizId: contentId || undefined }), '_blank');
                }
                break;
            }
            case CONTENT_TYPE.CATALOG:
            case CONTENT_TYPE.CATALOG_THEME: {
                if (subject) {
                    let path = teamId ? staticUrls.labLibrarySubject : staticUrls.deepLabLibrary;
                    if (contentId) {
                        path = teamId ? staticUrls.labLibrarySubjectTheme : staticUrls.deepLabTheme;
                    }

                    history.push(`${generatePath(path, { subject, themeId: contentId })}${search}`);
                }

                break;
            }
            case CONTENT_TYPE.COLLECTION: {
                const containerSlug = contentId;
                if (containerSlug && subject) {
                    history.push(generatePath(staticUrls.labLibrarySubjectCollection, { subject, containerSlug }));
                }

                break;
            }
            case CONTENT_TYPE.LINK: {
                if (url && typeof window !== 'undefined') {
                    window.open(url, is_open_in_new_tab ? '_blank' : '_self')?.focus();
                }

                break;
            }
        }
    };

    return (
        <div className="actual-content__activity">
            <ActivityTypeIcon>{icon && <Image alt="" src={icon} />}</ActivityTypeIcon>
            <div className="actual-content__activity-desc">
                <div className="actual-content__activity-title">
                    <Text typography={UI_KIT_TEXT_TYPOGRAPHY.paragraphXLMedium}>{title}</Text>
                </div>
                <div className="actual-content__activity-description">
                    <Text type={Text.TYPE.MARKDOWN} typography={UI_KIT_TEXT_TYPOGRAPHY.paragraphMRegular}>
                        {activityDescription}
                    </Text>
                </div>
            </div>
            <Button
                disabled={cantProceed}
                icon={cantProceed ? <CheckCircle /> : undefined}
                onClick={handleClick}
                progress={isLoading}
                rounded={true}
                size={Button.SIZE.M}
            >
                <SimpleText id={buttonTextId} />
            </Button>
        </div>
    );
};
